import React, { useState, useEffect } from 'react'
import { X, Phone, Mail, MessageCircle } from 'lucide-react'
import { companyService } from '../services/companyService'
import { useAuthStore } from '../stores/authStore'

const FloatingWhatsAppButton = () => {
  const { isAuthenticated } = useAuthStore()
  const [companyInfo, setCompanyInfo] = useState(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const info = await companyService.getCompanyInfo()
        if (info.contact_phone || info.contact_email) {
          setCompanyInfo(info)
          setIsVisible(true)
        }
      } catch (error) {
        setIsVisible(false)
      }
    }

    if (isAuthenticated) {
      fetchCompanyInfo()
    }
  }, [isAuthenticated])

  const handleButtonClick = () => {
    setIsSheetOpen(true)
  }

  const closeSheet = () => {
    setIsSheetOpen(false)
  }

  const getCleanPhone = () => {
    if (!companyInfo?.contact_phone) return null
    return companyInfo.contact_phone.replace(/\D/g, '')
  }

  const getFullPhone = () => {
    const cleanPhone = getCleanPhone()
    if (!cleanPhone) return null
    return cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`
  }

  const formatPhoneDisplay = (phone) => {
    if (!phone) return ''
    const clean = phone.replace(/\D/g, '')
    // Format as (XX) XXXXX-XXXX or (XX) XXXX-XXXX
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`
    } else if (clean.length === 10) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`
    }
    return phone
  }

  const handleWhatsApp = () => {
    const fullPhone = getFullPhone()
    if (!fullPhone) return
    const message = encodeURIComponent(`Olá! Preciso de ajuda.`)
    window.open(`https://wa.me/${fullPhone}?text=${message}`, '_blank')
    closeSheet()
  }

  const handleCall = () => {
    const cleanPhone = getCleanPhone()
    if (!cleanPhone) return
    window.open(`tel:${cleanPhone}`, '_self')
    closeSheet()
  }

  const handleEmail = () => {
    if (!companyInfo?.contact_email) return
    window.open(`mailto:${companyInfo.contact_email}`, '_self')
    closeSheet()
  }

  // Don't render if not authenticated or no contact info
  if (!isAuthenticated || !isVisible) {
    return null
  }

  return (
    <>
      {/* Floating Button - WhatsApp green for instant recognition */}
      <button
        onClick={handleButtonClick}
        className="fixed bottom-20 right-4 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
        aria-label={`Contato com ${companyInfo?.name || 'o salão'}`}
        title={`Contato com ${companyInfo?.name || 'o salão'}`}
      >
        {/* WhatsApp Icon */}
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>

      {/* Backdrop */}
      {isSheetOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300"
          onClick={closeSheet}
        />
      )}

      {/* Bottom Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[70] bg-brand-light dark:bg-brand-dark rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out ${
          isSheetOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-brand-dark/20 dark:bg-brand-light/20 rounded-full" />
        </div>

        {/* Close button */}
        <button
          onClick={closeSheet}
          className="absolute top-4 right-4 p-2 text-brand-dark/60 dark:text-brand-light/60 hover:text-brand-dark dark:hover:text-brand-light transition-colors"
          aria-label="Fechar"
        >
          <X size={24} strokeWidth={1.5} />
        </button>

        {/* Content */}
        <div className="px-6 pb-8 pt-2">
          {/* Logo and Name */}
          <div className="flex flex-col items-center mb-6">
            {companyInfo?.logo_url ? (
              <img
                src={companyInfo.logo_url}
                alt={companyInfo.name}
                className="w-20 h-20 object-contain rounded-2xl mb-3"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            ) : (
              <div className="w-20 h-20 bg-brand-dark/10 dark:bg-brand-light/10 rounded-2xl mb-3 flex items-center justify-center">
                <MessageCircle size={32} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              </div>
            )}
            <h2 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light text-center">
              {companyInfo?.name || 'Contato'}
            </h2>
            <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 mt-1">
              Entre em contato conosco
            </p>
          </div>

          {/* Contact Options */}
          <div className="space-y-3">
            {/* WhatsApp */}
            {companyInfo?.contact_phone && (
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center gap-4 p-4 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">WhatsApp</p>
                  <p className="text-sm text-white/80">{formatPhoneDisplay(companyInfo.contact_phone)}</p>
                </div>
              </button>
            )}

            {/* Phone Call */}
            {companyInfo?.contact_phone && (
              <button
                onClick={handleCall}
                className="w-full flex items-center gap-4 p-4 bg-brand-dark/10 dark:bg-brand-light/10 hover:bg-brand-dark/20 dark:hover:bg-brand-light/20 text-brand-dark dark:text-brand-light rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 rounded-full flex items-center justify-center">
                  <Phone size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">Ligar</p>
                  <p className="text-sm opacity-70">{formatPhoneDisplay(companyInfo.contact_phone)}</p>
                </div>
              </button>
            )}

            {/* Email */}
            {companyInfo?.contact_email && (
              <button
                onClick={handleEmail}
                className="w-full flex items-center gap-4 p-4 bg-brand-dark/10 dark:bg-brand-light/10 hover:bg-brand-dark/20 dark:hover:bg-brand-light/20 text-brand-dark dark:text-brand-light rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 rounded-full flex items-center justify-center">
                  <Mail size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">E-mail</p>
                  <p className="text-sm opacity-70 truncate">{companyInfo.contact_email}</p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Safe area padding for iOS */}
        <div className="h-safe-area-bottom" />
      </div>
    </>
  )
}

export default FloatingWhatsAppButton
